import { Button, Input, message, Radio, Select, SelectProps, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useQuery } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';
import { syncVoucher, voucherList, VoucherListParams } from 'services/api/conversation';
import Empty from 'components/Empty';
import { VoucherCard } from 'components/VoucherCard';
import { useRequest, useToggle } from 'ahooks';
import { VoucherModal } from 'views/Conversation/Sider/VoucherDrawer/VoucherModal';
import { useActivityContext } from '@knowledge/Activity/ActivityContext';
import { isTiKTok as isTiKTokUtil } from 'lib/utils/isTikTok';
import { useVoucherOperate } from 'hooks/business/useVoucherOperate';

interface VoucherListPanelProps {
  store: Knowledge.Activity.Store;
  value?: Knowledge.Activity.Voucher;
  onChange?: (value: Knowledge.Activity.Voucher) => void;
}

type ListParams = Omit<VoucherListParams, 'internal_store_id'>;

export const VoucherListPanel = (props: VoucherListPanelProps) => {
  const { store, value, onChange } = props;
  const { storeId, region, platform } = store;
  const { t } = useTranslate();
  const { storeMap } = useActivityContext();

  const targetStore = storeMap?.[storeId];
  const storeName = targetStore?.alias || targetStore?.storeName || '-';

  const [modalVisible, { toggle: toggleModalVisible }] = useToggle();
  const [searchParams, setSearchParams] = useState<ListParams>({
    keywordType: 0,
  });
  const [keyword, setKeyword] = useState('');
  const { canCreate } = useVoucherOperate(undefined, {
    channel: targetStore?.externalType,
  });
  const isTiktok = isTiKTokUtil(targetStore?.externalType);

  const params: VoucherListParams = useMemo(
    () => ({
      ...searchParams,
      internal_store_id: storeId,
      keywordType: searchParams.searchKeyword ? searchParams.keywordType : undefined,
    }),
    [storeId, searchParams],
  );

  // 查询可用的优惠券列表
  const {
    isLoading,
    isRefetching,
    data: list,
    refetch,
  } = useQuery({
    queryKey: [ApiQueryKey.VoucherList, params],
    queryFn: ({ queryKey }) => {
      const params = queryKey?.[1] as VoucherListParams;
      if (isTiktok) {
        return voucherList({
          ...params,
          voucher_status: 'ONGOING',
        }).then((response) => response?.data?.data);
      } else {
        if (params.voucher_status) {
          return voucherList(params).then((response) => response?.data?.data);
        }

        // 通过两个状态筛选列表查询再合并
        return Promise.all([
          voucherList({
            ...params,
            voucher_status: 'ONGOING',
          }),
          voucherList({
            ...params,
            voucher_status: 'SCHEDULED',
          }),
        ]).then(([res1, res2]) => {
          const EMPTY_ARRAY: Conversation.Voucher.Vo[] = [];
          const data1 = res1?.data?.data ?? EMPTY_ARRAY;
          const data2 = res2?.data?.data ?? EMPTY_ARRAY;
          return [...data1, ...data2];
        });
      }
    },
  });

  const sortList = useMemo(() => {
    return list?.sort((a, b) => {
      const aSurplus = a.usageQuantity - a.currentUsage;
      const bSurplus = b.usageQuantity - b.currentUsage;
      return aSurplus > bSurplus ? -1 : 1;
    });
  }, [list]);

  const { runAsync: sync, loading: syncLoading } = useRequest(
    () => syncVoucher({ internalStoreId: storeId }),
    {
      manual: true,
      onSuccess() {
        message.success(t('voucher.优惠券数据更新成功'));
        refetch();
      },
      onError() {
        message.error(t('voucher.更新失败，请稍后重试'));
      },
    },
  );

  const statusOptions: SelectProps['options'] = useMemo(
    () => [
      {
        label: t('status.ONGOING'),
        value: 'ONGOING',
      },
      {
        label: t('status.SCHEDULED'),
        value: 'SCHEDULED',
      },
    ],
    [t],
  );

  const searchOptions: SelectProps['options'] = useMemo(() => {
    const result = [
      {
        label: t('searchOption.name'),
        value: 0,
      },
      {
        label: t('searchOption.discount'),
        value: 1,
      },
      {
        label: t('searchOption.discountPercent'),
        value: 2,
      },
    ];
    if (isTiktok) {
      return result;
    }

    return [
      ...result,
      {
        label: t('searchOption.code'),
        value: 3,
      },
    ];
  }, [isTiktok, t]);

  return (
    <Spin spinning={isLoading || isRefetching} wrapperClassName="flex-1 h-full overflow-y-auto">
      <div className="flex-1 flex h-full flex-col relative">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex gap-3">
            {!isTiktok && (
              <Select
                placeholder={t('voucher_status')}
                className="w-40"
                options={statusOptions}
                allowClear
                onChange={(value) =>
                  setSearchParams((prev) => ({ ...prev, voucher_status: value }))
                }
              />
            )}
            <Input.Group compact>
              <Select
                className="w-5/12"
                options={searchOptions}
                value={searchParams.keywordType}
                onChange={(value) => setSearchParams((prev) => ({ ...prev, keywordType: value }))}
              />
              <Input
                className="!w-7/12"
                placeholder={t('voucher.searchPlaceholder')}
                value={keyword}
                onPressEnter={() =>
                  setSearchParams((prev) => ({ ...prev, searchKeyword: keyword }))
                }
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Input.Group>
          </div>
          <div>
            <Button type="link" loading={syncLoading} onClick={sync}>
              {t('sync_voucher')}
            </Button>
            {canCreate && (
              <Button type="link" onClick={toggleModalVisible}>
                +{t('form.addTitle')}
              </Button>
            )}
          </div>
        </div>
        <div className="flex-1 px-4 py-2">
          {sortList && sortList.length > 0 ? (
            <Radio.Group
              className="!grid grid-cols-2 gap-4"
              value={value?.internalVoucherId}
              onChange={(e) => {
                const found = sortList.find((i) => i.internalVoucherId === e.target.value);
                if (found) {
                  const { internalVoucherId, externalVoucherId, voucherCode } = found;
                  onChange?.({
                    storeId,
                    internalVoucherId,
                    externalVoucherId,
                    voucherCode,
                  });
                }
              }}
            >
              {sortList.map((item) => {
                const { internalVoucherId } = item;
                return (
                  <VoucherCard
                    storeId={`${storeId}`}
                    key={internalVoucherId}
                    voucher={item}
                    before={
                      <div className="flex h-full items-center ml-2">
                        <Radio value={internalVoucherId} />
                      </div>
                    }
                  />
                );
              })}
            </Radio.Group>
          ) : (
            <Empty className="!mt-16" />
          )}
        </div>
        {modalVisible && (
          <VoucherModal
            title={t('form.addTitle')}
            storeId={`${storeId}`}
            storeName={storeName}
            region={region}
            visible={modalVisible}
            platform={platform}
            onCancel={toggleModalVisible}
            onSuccess={() => {
              toggleModalVisible();
              refetch();
            }}
          />
        )}
      </div>
    </Spin>
  );
};
